import { ActionsProxy, NodesProxy } from "@/wc/registry/lib/dom/index.js";
// import fetcher from "@/wc/registry/lib/fetcher/index.js";

// Create a style element to be injected into the element or shadow DOM.
// This way the styles can immediately be applied. Avoids a Flash of Unstyled
// Content (FUOC).
function createStyleElement(css: string) {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = css;
    return styleElement;
}

// The modifiedConnectedCallBack takes a connectedCallback, and modifies it
// by returning that same connectedCallback (if it exists), but makes sure
// that it executes some code against the context of the component where this
// function is executed. It adds an HTMLTemplateElement to the shadow root,
// and adds a styleElement. Doing it 'this magic way' avoids the need to
// have to redefine this same code in all our custom elements that make
// use of the customElement wrapper.
function modifiedConnectedCallBack(
    connectedCallback: (() => Promise<void>) | undefined,
    template: HTMLTemplateElement | undefined,
    orderedStyles: HTMLElement[],
    shadowRoot: boolean
) {
    // Explicitly not using an arrow function, since that will inject the context
    // of this function instead of allowing the function to be executed in the
    // to-be-created context.
    return function () {
        if (!this.connectedCallbackCalledCount) {
            this.connectedCallbackCalledCount = 0;
        }

        // When the connectedCallbackCalledCount is bigger than 0, it might be that
        // the element is moved within the DOM or it might be that the element
        // is simply re-attached. In those cases we can skip re-appending the
        // styles and templates. The original connectedCallback is still called
        // to avoid unexpected behavior; and it might be that the writer of the
        // component wants to do things differently depending on context. (e.g.
        // the new position in the DOM tree)
        if (this.connectedCallbackCalledCount === 0) {
            // By default, use the shadowRoot. However, when someone explicitly
            // passes 'avoid-shadow-root' to the dataset or as an option to the
            // decorator, do not use the shadow root.
            const useShadowRoot =
                typeof this.dataset.avoidShadowRoot === "undefined" &&
                shadowRoot;
            if (useShadowRoot) {
                this.attachShadow({ mode: "open" });
            }
            this.target = useShadowRoot ? this.shadowRoot : this;

            this.actions = ActionsProxy(this.target);
            this.nodes = NodesProxy(this.target);

            // Attach the pre-created style elements directly to the shadow root.
            // The style elements only apply to this component because of the nature
            // of the Shadow DOM.
            orderedStyles.forEach((styleElement) => {
                this.target.appendChild(styleElement.cloneNode(true));
            });

            if (template) {
                this.target.appendChild(template.content.cloneNode(true));
            }
        }

        this.connectedCallbackCalledCount += 1;

        // If the connectedCallback exists, call it while applying 'this' (the
        // context of the component that is attached in the DOM).
        if (connectedCallback) {
            connectedCallback.apply(this);
        }
    };
}

export interface ICustomElementOptions {
    style?: Promise<string>;
    // When this is set, the component will try to fetch the external.css file
    // from the component folder. This external.css is typically generated by
    // the bundler because a external.ts file utilizes css from a package.
    externalStyle?: Promise<string>;
    template?: Promise<HTMLTemplateElement | Element | null>;
    // In most cases it is advisable to use a shadow root, this way the styles
    // defined in the component won't affect styles outside of this component.
    //
    // There are some exceptions where it is okay to turn it off. E.g. when you
    // want to allow password managers to prefill passwords in a password fields,
    // then you don't want to 'hide' the input fields by using the shadow root.
    shadowRoot?: boolean;
    // When there are extra styles that you want to have applied to this
    // component, this is the place to pass references to those styles.
    extraStyles?: string[];
}

// Decorator function to give a name for a custom element.
// Extend the given custom element with some logic to fetch and pass
// the related html template and tagName.
export default function customElement(
    tagName: string,
    {
        shadowRoot = true,
        extraStyles = [],
        style = undefined,
        externalStyle = undefined,
        template = undefined,
    }: ICustomElementOptions = {}
) {
    // Pretty sure it should be any.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return async function prepareClass<T extends { new (...args: any[]) }>(
        constr: T
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore because tslint doesn't seem to get get async class decorator types
    ): void {
        // The place where all resolved fetches store their fetched style and
        // template.
        interface PromiseResults {
            style: string | undefined;
            themeStyle: string | undefined;
            externalStyle: string | undefined;
            template: HTMLTemplateElement | undefined;
        }
        const promiseResults: PromiseResults = {
            style: undefined,
            themeStyle: undefined,
            externalStyle: undefined,
            template: undefined,
        };

        // slice to remove 'c-' from the tagname
        //const componentName = tagName.slice(2);

        // The fetches that are added to this list return a promise. The fetches
        // are immediately initiated, but can take some time. They are executed
        // in parallel, where this list can be used to check if all the fetches
        // have completed in a later stage.
        const fetchPromises = [];
        if (style) {
            fetchPromises.push(
                style.then((style) => {
                    promiseResults.style = style;
                })
            );
        }

        if (template) {
            fetchPromises.push(
                template.then((template) => {
                    // check if HTMLTemplateElement
                    if (template instanceof HTMLTemplateElement) {
                        promiseResults.template = template;
                    } else {
                        throw Error(
                            `Template for '${tagName}' is not an instance of HTMLTemplateElement`
                        );
                    }
                })
            );
        }

        // When externalCSS is true, the css file is taken from
        // @/stekz-wc/registry/component/{componentName}/external.css and injected in the
        // style element.
        if (externalStyle) {
            fetchPromises.push(
                externalStyle.then((externalStyle) => {
                    promiseResults.externalStyle = externalStyle;
                })
            );
        }

        // All fetches are already in progress in this stage. The promise.all makes
        // sure that we wait until all the fetches have completed. When they have,
        // we can safely use promiseResults, as they have been populated by the
        // completed promises. This way we can have multiple fetches going on
        // in parallel.
        await Promise.all(fetchPromises);

        // Create a reference to the template, which can then be passed to the
        // component, which can then inject it into its own shadowRoot.
        const templateRef = promiseResults?.template;

        // Add all the style elements in a list, so it can be passed to the
        // component, which can then inject it into its own shadowRoot.
        const orderedStyles = [];
        // ordered styles array should be in the following order:
        // ['external css', 'component specific css']
        // this will make us override the parent styles without having to use the
        // '!important' property
        if (externalStyle) {
            orderedStyles.push(
                createStyleElement(promiseResults.externalStyle)
            );
        }
        orderedStyles.push(createStyleElement(promiseResults.style));
        if (extraStyles) {
            orderedStyles.push(createStyleElement(extraStyles.join("")));
        }

        // Modifying the connectedCallback of the component, to secretly create
        // a shadow root, inject a template and some styles. Previously we did
        // this through modifying the constructor, but this not conforming with
        // the conformance spec for custom element constructors, even though
        // browsers supported this. See specificaly point 1 and 5 here:
        // https://html.spec.whatwg.org/multipage/custom-elements.html#custom-element-conformance
        //
        // To comply with this spec, and to avoid having to introduce extra code
        // in all our components, we modify the connectedCallback of each
        // component class, to make sure that the appropriate boilerplating is
        // executed as soon as the passed component is connected to the DOM.
        const connectedCallback = constr.prototype.connectedCallback;
        constr.prototype.connectedCallback = modifiedConnectedCallBack(
            connectedCallback,
            templateRef,
            orderedStyles,
            shadowRoot
        );
        customElements.define(tagName, constr);
    };
}
